import { useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ProductDetails, GenderSuitability } from '../../types/api';
import ProductPrice from '../ProductPrice/ProductPrice';
import DeliveryEstimate from '../DeliveryEstimate/DeliveryEstimate';
import AddToCartControl from '../AddToCartControl/AddToCartControl';
import WishlistButton from '../WishlistButton/WishlistButton';
import NotifyMeButton from '../NotifyMe/NotifyMeButton';
import ProductGallery from '../ProductGallery/ProductGallery';
import './ProductDetailsPage.css';
import { useStorefrontContent } from '../../hooks/useStorefrontContent';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useProductDetails } from './useProductDetails';

const suitabilityLabels: Readonly<Record<GenderSuitability, string>> = {
  unisex: 'Unisex',
  women: 'Women',
  men: 'Men',
};
function formatDimensions(product: Readonly<ProductDetails>): string {
  if (product.length_cm == null || product.width_cm == null) return '';
  const length = Number(product.length_cm);
  const width = Number(product.width_cm);
  return Number.isFinite(length) && Number.isFinite(width) && length > 0 && width > 0
    ? `${length} × ${width} cm`
    : '';
}
export default function ProductDetailsPage() {
  const { itemId } = useParams();
  const { product, error, refreshError, unavailable, refresh } = useProductDetails(itemId);
  const { content: copy } = useStorefrontContent();
  const focusedItem = useRef<string | undefined>(undefined);
  const heading = useRef<HTMLHeadingElement>(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [itemId]);

  useDocumentTitle(product?.display_name ?? 'Product');

  // Move focus to the heading once per product, as soon as the page settles.
  useEffect(() => {
    const settled = product?.item_id === itemId || error || unavailable;
    if (!settled || focusedItem.current === itemId) return;
    heading.current?.focus({ preventScroll: true });
    focusedItem.current = itemId;
  }, [product, error, unavailable, itemId]);

  const soldOut = product?.quantity === 0 || product?.in_stock === false;
  const details: Array<[string, string]> = product
    ? [
        ['Dimensions', formatDimensions(product)],
        ['Size', product.size_details?.trim() ?? ''],
        ['Suitable for', product.gender_suitability ? suitabilityLabels[product.gender_suitability] : ''],
        ['Fabric', product.fabric.join(', ')],
        ['Color', product.color.join(', ')],
        ['Product type', product.product_type],
        ['Style / work', product.style.join(', ')],
        ['Country of origin', product.country_of_origin ?? ''],
      ]
    : [];

  return (
    <section className="product-page" aria-label="Product details">
      <nav className="product-page__breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link> / {product?.display_name ?? 'Product'}
      </nav>
      {refreshError && (
        <p role="alert">
          {refreshError} <button onClick={refresh}>Retry refresh</button>
        </p>
      )}
      {unavailable ? (
        <h1 ref={heading} tabIndex={-1}>
          Product unavailable
        </h1>
      ) : error ? (
        <div role="alert">
          <h1 ref={heading} tabIndex={-1}>
            {error}
          </h1>
          <button onClick={refresh}>Try again</button>
        </div>
      ) : !product ? (
        <p role="status">Loading product…</p>
      ) : (
        <div className="product-details">
          <ProductGallery
            images={product.image_urls}
            mainImage={product.image_url}
            alt={product.display_name}
          />
          <div className="product-details__info">
            <h1 ref={heading} tabIndex={-1} className="product-details__title">
              {product.display_name}
            </h1>
            <p className="product-details__price">
              <ProductPrice pricing={product.pricing} price={product.price} />
            </p>
            <p>
              {product.product_type} · {product.fabric.join(', ')} · {product.color.join(', ')}
            </p>
            <p>Earn {product.pricing?.purchase_points ?? Math.floor(product.price)} points on delivery</p>
            <p>{soldOut ? 'Sold out' : 'Available to order'}</p>
            {!soldOut && <DeliveryEstimate />}
            <div className="product-details__actions">
              <AddToCartControl
                product={product}
                soldOut={soldOut}
                soldOutAction={
                  <NotifyMeButton
                    itemId={product.item_id}
                    name={product.display_name}
                    className="product-details__add"
                  />
                }
                buttonClassName="product-details__add"
                stepperClassName="product-details__stepper"
              />
              <WishlistButton itemId={product.item_id} name={product.display_name} withLabel />
            </div>
            {soldOut && (
              <p className="product-details__hint">
                Sold out for now. Tap Notify me and we&apos;ll email you once it&apos;s back.
              </p>
            )}
            {product.description.trim() && (
              <>
                <details open>
                  <summary>Description</summary>
                  <p className="product-details__description">{product.description}</p>
                </details>
              </>
            )}
            <details>
              <summary>Specifications</summary>
              <dl className="product-details__details">
                {details
                  .filter(([, value]) => value)
                  .map(([label, value]) => (
                    <div key={label} className="product-details__detail">
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
              </dl>
            </details>
            {product.care_instructions && (
              <details>
                <summary>Care instructions</summary>
                <p>{product.care_instructions}</p>
              </details>
            )}
            {(
              [
                ['brand', 'About Resham Tales'],
                ['shipping', 'Delivery'],
                ['returns', 'Returns and refunds'],
              ] as const
            ).map(([key, title]) =>
              copy[key] ? (
                <details key={key}>
                  <summary>{title}</summary>
                  <p>{copy[key]}</p>
                </details>
              ) : null,
            )}
          </div>
        </div>
      )}
    </section>
  );
}
